import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom'
import { t } from '../i18n'
import { useTrending } from '../State/refData'
import { useUi } from '../State/Ui'
import AppBar from './AppBar'
import Footer from './Footer'
import { BottomNav, LeftNav } from './Nav'
import { sidebarAdUnit } from '../Utils/ads'
import AdSlot from './AdSlot'
import { ConsentBanner, QrModal, Toast } from './Overlays'
import SearchBox from './SearchBox'
import { TrendingList } from './Trending'

/* What the desktop right column shows (spec 4.3). Pages adjust it:
   - Istraži and empty "Moje teme": nothing at all (they show trending
     themselves); the column keeps its width so nothing shifts
   - a category chip on Početna: "Aktuelno · {Kategorija}"
   - Križaljka: the page's own content (its clues) above the footer, put
     there with a portal into useRailSlot()'s element
   - ad: the feed pages' surface name; they get the sticky 300×250 under
     Aktuelne teme when the config's "sidebar" switch is on (ads spec 2.2) */
const DEFAULT_RAIL = { empty: false, search: true, category: null, slot: false, ad: null }
const RailContext = createContext(() => {})
const RailSlotContext = createContext(null)

/* The element a page with useRail({ slot: true }) portals its column into
   (null until it's on the page, and always below 1100 px). */
export function useRailSlot() {
  return useContext(RailSlotContext)
}

export function useRail(config) {
  const setRail = useContext(RailContext)
  const key = JSON.stringify(config)
  useEffect(() => {
    setRail({ ...DEFAULT_RAIL, ...JSON.parse(key) })
    return () => setRail(DEFAULT_RAIL)
  }, [key, setRail])
}

function RightRail({ rail, slotRef, noRail }) {
  const location = useLocation()
  const general = useTrending({ limit: 5, enabled: !rail.empty && !rail.slot })
  const byCategory = useTrending({
    category: rail.category?.slug,
    limit: 5,
    enabled: !rail.empty && !rail.slot && Boolean(rail.category),
  })
  if (rail.empty) return <aside className="right-rail" aria-hidden="true" />
  if (rail.slot)
    return (
      <aside className="right-rail">
        <div className="right-rail-inner">
          <div ref={slotRef} />
          <Footer />
        </div>
      </aside>
    )
  const useCategory = rail.category && byCategory.topics.length > 0
  // Under 1100 px the column is hidden, and with it its ad.
  const adUnit = rail.ad && !noRail ? sidebarAdUnit() : null
  return (
    <aside className="right-rail">
      <div className="right-rail-inner">
        {rail.search && <SearchBox />}
        <TrendingList
          topics={useCategory ? byCategory.topics : general.topics}
          title={
            useCategory
              ? t('trending.categoryTitle', { category: rail.category.title })
              : t('trending.title')
          }
        />
        {adUnit && (
          <AdSlot
            key={location.pathname + location.search}
            variant="sidebar"
            unit={adUnit}
            surface="sidebar"
            position={1}
          />
        )}
        <Footer />
      </div>
    </aside>
  )
}

export default function Layout() {
  const { isMobile, noRail } = useUi()
  const [rail, setRail] = useState(DEFAULT_RAIL)
  const [slot, setSlot] = useState(null)
  const railSetter = useMemo(() => setRail, [])

  return (
    <RailContext.Provider value={railSetter}>
      <RailSlotContext.Provider value={noRail ? null : slot}>
        <a className="skip-link" href="#main">
          {t('common.home')}
        </a>
        {isMobile && <AppBar />}
        <div className="shell">
          {!isMobile && <LeftNav />}
          <main className="main-col" id="main">
            <Outlet />
          </main>
          {!isMobile && <RightRail rail={rail} slotRef={setSlot} noRail={noRail} />}
        </div>
        {isMobile && <BottomNav />}
        <QrModal />
        <Toast />
        <ConsentBanner />
        <ScrollRestoration />
      </RailSlotContext.Provider>
    </RailContext.Provider>
  )
}
